import React, { useCallback, useRef, useState, useEffect, useContext } from "react";
import { GoogleMap, Autocomplete, DrawingManager, useJsApiLoader, InfoWindowF, Marker } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_VERSION } from "../../../Library/constants";
import { sphericalCosinus } from "../../../Library/geodist";
import "./styles.css";
import { RippleButton } from "../../Atoms";
import { BorderedTick, DeleteIcon, PenIcon, SearchIcon } from "../../../Assets/images/svgs";
import { Input } from "@chakra-ui/react";
import { StoreContext } from "../../../Library/contexts";

const containerStyle = {
  width: "100%",
  height: "500px"
};

const center = {
  lat: 20.5937,
  lng: 78.9629
};

const libraries = ["places", "drawing", "geometry"];

const GooglePolylineDrawer = ({ onRoutePoint, initialRoutePoints = [] }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
    version: GOOGLE_MAPS_VERSION,
  });

  const mapRef = useRef(null);
  const polylineRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const inputRef = useRef(null);

  const [searchBox, setSearchBox] = useState(null);
  const [coordinates, setCoordinates] = useState([]);
  const [distance, setDistance] = useState("0");
  const [isDrawingActive, setIsDrawingActive] = useState(true);

  const [isResuming, setIsResuming] = useState(false);
  const [ enableStopBtn, setEnableStopBtn ] = useState(false);
  const [ showPointRenameField, setShowPointRenameField ] = useState(false);
  const [ pointNewName, setPointNewName ] = useState("");
  const [ markerPoistion, setMarkerPoistion ] = useState(null);
  const ghostPolylineRef = useRef(null);
  const drawingPointsRef = useRef([]);
  const distMouseUpListener = useRef(null);
  const isLinePreDrawn = useRef(false);
  const coordinatesRef = useRef([]);

  const [infoWindow, setInfoWindow] = useState({
    isOpen: false,
    position: null,
    vertexIndex: null,
  });
  const selectedVertexRef = useRef(null);
  const infoWindowClickListener = useRef(null);

  const { lastSearchedLoc, setLastSearchedLoc } = useContext(StoreContext);

  useEffect(() => {
    if (!isLoaded || !initialRoutePoints || !initialRoutePoints.length || polylineRef.current || !mapRef.current || isLinePreDrawn.current) return;
    const map = mapRef.current;
    const path = initialRoutePoints.map((coord) => new window.google.maps.LatLng(coord.lat, coord.lng));

    const newPolyline = new window.google.maps.Polyline({
      path,
      map,
      strokeColor: "#00A8E2",
      strokeOpacity: 1.0,
      strokeWeight: 2,
      editable: true,
    });

    polylineRef.current = newPolyline;

    const polyPath = newPolyline.getPath();
    polyPath.addListener("set_at", () => getPlottedCoordinates(polyPath));
    polyPath.addListener("insert_at", () => getPlottedCoordinates(polyPath));
    polyPath.addListener("remove_at", () => getPlottedCoordinates(polyPath));

    let coords = initialRoutePoints.map(point => { point.label = point.title; return point; });
    setCoordinates(coords);
    coordinatesRef.current = coords;
    setIsDrawingActive(true)
    setIsResuming(true);
    setEnableStopBtn(true);

    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(p => bounds.extend(p));
    map.fitBounds(bounds);
    isLinePreDrawn.current = true;

  }, [isLoaded, initialRoutePoints, mapRef.current]);

  const addDistMouseUpListener = (map = mapRef.current) => {
    distMouseUpListener.current = map.addListener("mouseup", (event) => {
      drawingPointsRef.current.push(event.latLng);

      if (drawingPointsRef.current.length > 1) {
        let temporaryDistance = 0;
        for (let i = 0; i < drawingPointsRef.current.length - 1; i++) {
          const p1 = drawingPointsRef.current[i];
          const p2 = drawingPointsRef.current[i + 1];
          temporaryDistance += sphericalCosinus(p1.lat(), p1.lng(), p2.lat(), p2.lng());
        }
        drawingPointsRef.current = drawingPointsRef.current.map((coord, i) => {
          coord.label = `Point ${i+1}`;
          return coord;
        });

        setDistance((temporaryDistance / 1000).toFixed(2));
        onRoutePoint((temporaryDistance / 1000).toFixed(2), drawingPointsRef.current);
        if (!polylineRef.current) {
          stopDrawing();
          resumeDrawing();
          if (polylineRef.current) polylineRef.current.getPath().push(event.latLng);
        }
      } else {
        drawingPointsRef.current[0].label = "Point 1";
        onRoutePoint("0", drawingPointsRef.current);
        setEnableStopBtn(true);
      }
    });
  }

  const removeDistMouseUpListener = () => {
    if (distMouseUpListener.current) {
      window.google.maps.event.removeListener(distMouseUpListener.current);
      distMouseUpListener.current = null;
    }
  }

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (lastSearchedLoc) mapRef.current.fitBounds(lastSearchedLoc);
    addDistMouseUpListener(mapRef.current);
  }, [isDrawingActive]);

  const getPlottedCoordinates = useCallback((path) => {
    const coords = path.getArray().map((latLng, i) => ({
      lat: latLng.lat(),
      lng: latLng.lng(),
      label: (coordinatesRef.current[i] && coordinatesRef.current[i].label) ? coordinatesRef.current[i].label : `Point ${i + 1}`,
    }));

    let dst = 0;
    for (let i = 0; i < coords.length - 1; i++) {
      const p1 = coords[i];
      const p2 = coords[i + 1];
      dst += sphericalCosinus(p1.lat, p1.lng, p2.lat, p2.lng);
    }

    setCoordinates(coords);
    setDistance((dst / 1000).toFixed(2));
    onRoutePoint((dst / 1000).toFixed(2), coords);
  }, [onRoutePoint, coordinates]);

  useEffect(() => {
    if (!isResuming || !mapRef.current || !polylineRef.current) {
      return;
    }

    ghostPolylineRef.current = new window.google.maps.Polyline({
      map: mapRef.current,
      strokeColor: '#00A8E2',
      strokeOpacity: 0.5,
      strokeWeight: 2,
      clickable: false,
    });

    const moveListener = mapRef.current.addListener('mousemove', (event) => {
      const path = polylineRef.current.getPath();
      if(path.getLength() > 0) {
        const lastPoint = path.getAt(path.getLength() - 1);
        ghostPolylineRef.current.setPath([lastPoint, event.latLng]);
      }
    });

    const clickListener = mapRef.current.addListener('click', (event) => {
      const path = polylineRef.current.getPath();
      path.push(event.latLng);
      getPlottedCoordinates(path);
    });

    const polylineClickListener = polylineRef.current.addListener('click', (event) => {
      const path = polylineRef.current.getPath();
      if (event.vertex === 0 && path.getLength() > 2) {

        path.push(path.getAt(0));
        getPlottedCoordinates(path);
        stopDrawing();
      }
    });


    return () => {
      window.google.maps.event.removeListener(clickListener);
      window.google.maps.event.removeListener(moveListener);
      window.google.maps.event.removeListener(polylineClickListener);
      if (ghostPolylineRef.current) {
        ghostPolylineRef.current.setMap(null);
        ghostPolylineRef.current = null;
      } 
    };
  }, [isResuming, getPlottedCoordinates]);

  const addInfoWindowClickListener = () => {
    if (!polylineRef.current) return;
    infoWindowClickListener.current = polylineRef.current.addListener("click", (event) => {
      if (event.vertex !== null && event.vertex !== undefined) {
        selectedVertexRef.current = event.vertex;
        setInfoWindow({
          isOpen: true,
          position: event.latLng.toJSON(),
          vertexIndex: event.vertex,
        });
      }
    });
  }

  const removeInfoWindowClickListener = () => {
    if (infoWindowClickListener.current) {
      window.google.maps.event.removeListener(infoWindowClickListener.current);
      infoWindowClickListener.current = null;
    }
  }

  const onPolylineComplete = (polyline) => {
    polylineRef.current = polyline;
    polyline.setEditable(true);

    const path = polyline.getPath();
    path.addListener("set_at", () => getPlottedCoordinates(path));
    path.addListener("insert_at", () => getPlottedCoordinates(path));
    path.addListener("remove_at", () => getPlottedCoordinates(path));

    getPlottedCoordinates(path);
    setIsDrawingActive(false);

    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(null);
    }

    removeDistMouseUpListener()
  };

  useEffect(() => {
    if (isDrawingActive) removeInfoWindowClickListener();
    else addInfoWindowClickListener();
  }, [isDrawingActive]);

  const handleInfoWindowSelection = (event) => {
    if (event.vertex === infoWindow.vertexIndex) return setInfoWindow({ isOpen: false, position: null, vertexIndex: null });
    selectedVertexRef.current = event.vertex;
    setInfoWindow({
      isOpen: true,
      position: { lat: event.lat, lng: event.lng },
      vertexIndex: event.vertex,
    });
  }

  useEffect(() => {
    global.EE.addEventListener(global.EE.EVENT_TYPES.ROUTE_POINT_CLICKED, handleInfoWindowSelection);

    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.ROUTE_POINT_CLICKED, handleInfoWindowSelection);
    }
  }, [ infoWindow ]);

  const stopDrawing = () => {
    handleCloseInfoWindow();
    if (isResuming) {
        setIsResuming(false);
    } else if (polylineRef.current) {
        const path = polylineRef.current.getPath();
        if (path.getLength() > 0) {
          path.removeAt(path.getLength() - 1);
        }
        getPlottedCoordinates(path);
    }

    setIsDrawingActive(false);

    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(null);
    }

    removeDistMouseUpListener();
  };

  const resumeDrawing = () => {
    handleCloseInfoWindow();
    if (drawingManagerRef.current) drawingManagerRef.current.setDrawingMode(window.google.maps.drawing.OverlayType.POLYLINE);
    removeInfoWindowClickListener();
    setIsResuming(true);
    setIsDrawingActive(true);
  };

  const clearDrawing = () => {
    if (isDrawingActive || !polylineRef.current) return;
    handleCloseInfoWindow();

    polylineRef.current.setMap(null);
    polylineRef.current = null;
    drawingPointsRef.current = [];

    setIsResuming(false);
    setIsDrawingActive(true);
    setCoordinates([]);
    setDistance("0");
    onRoutePoint("0", []);
    addDistMouseUpListener();

    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(null);
    }
  };
  
  const handlePlaceChanged = () => {
    if (searchBox !== null) {
      const place = searchBox.getPlace();
      if (place.geometry && mapRef.current) {
        mapRef.current.fitBounds(place.geometry.viewport);
        setLastSearchedLoc(place.geometry.viewport)
      }
    }
  };

  const handleCloseInfoWindow = () => {
    setInfoWindow({ isOpen: false, position: null, vertexIndex: null });
    selectedVertexRef.current = null;
  };

  const handleRemovePoint = () => {
    if (polylineRef.current && selectedVertexRef.current !== null) {
      const path = polylineRef.current.getPath();
      path.removeAt(selectedVertexRef.current);
      handleCloseInfoWindow();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const tag = event.target.tagName.toLowerCase();
      const isTyping =
        tag === 'input' ||
        tag === 'textarea' ||
        event.target.isContentEditable;

      if (isTyping) return;

      if (event.key === 'Backspace') {
        if ((isDrawingActive || isResuming) && polylineRef.current) {
          const path = polylineRef.current.getPath();

          if (path.getLength() > 0) {
            path.removeAt(path.getLength() - 1);
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawingActive, isResuming]);

  const handlePointRename = () => {
    const updatedCoords = coordinates.map((coord, index) =>
      index === infoWindow.vertexIndex ? { ...coord, label: pointNewName } : coord
    );
    setCoordinates(updatedCoords);
    coordinatesRef.current = updatedCoords;
    onRoutePoint(distance, updatedCoords);
    setShowPointRenameField(false);
  }

  const handleManualLatLng = () => {
    const value = inputRef.current.value;
    const match = value.trim().match(/^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/);

    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[3]);
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(new window.google.maps.LatLng(lat, lng));
      mapRef.current.fitBounds(bounds);
      mapRef.current.setZoom(15)
      setMarkerPoistion({ lat, lng });
    } else setMarkerPoistion(null);
  };

  return (
    <div className="route-map-container">
      {isLoaded ? (
        <div className="route-map">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={5}
            onLoad={onMapLoad}
            options={{ mapTypeControl: false, streetViewControl: false }}
          >
            <Autocomplete onLoad={setSearchBox} onPlaceChanged={handlePlaceChanged}>
              <div className="route-map-search-input-wrapper">
                <input
                  type="text"
                  placeholder="Search Location"
                  className="route-map-search-input"
                  ref={inputRef}
                  onChange={handleManualLatLng}
                />
                <SearchIcon />
              </div>
            </Autocomplete>

            {infoWindow.isOpen && (
              <InfoWindowF
                position={infoWindow.position}
                onCloseClick={handleCloseInfoWindow}
              >
                <div>
                  <div>
                    <div className="route-info-window-box">
                      {!showPointRenameField ? <>
                        <p>{coordinates[infoWindow.vertexIndex]?.label || `Point ${infoWindow.vertexIndex + 1}`}</p>
                        <button onClick={() => {setShowPointRenameField(true); setPointNewName(coordinates[infoWindow.vertexIndex]?.label || `Point ${infoWindow.vertexIndex + 1}`)}}>
                          <PenIcon />
                        </button>
                        <button onClick={handleRemovePoint}>
                          <DeleteIcon className="del-point-icon" />
                        </button>
                      </> : <>
                        <Input maxLength={30} variant={"unstyled"} className="rename-field" placeholder="Give this stop a name" value={pointNewName} onChange={(e) => setPointNewName(e.target.value)} />
                        <button className="rename-btn" onClick={handlePointRename}>
                          <BorderedTick />
                        </button>
                      </>}
                    </div>
                  </div>
                </div>
              </InfoWindowF>
            )}

            {!polylineRef.current && (
              <DrawingManager
                onLoad={(dm) => { drawingManagerRef.current = dm; }}
                drawingMode={window.google.maps.drawing.OverlayType.POLYLINE}
                options={{
                  drawingControl: false,
                  polylineOptions: {
                    strokeColor: "#00A8E2",
                    strokeWeight: 2,
                    editable: true,
                  },
                }}
                onPolylineComplete={onPolylineComplete}
              />
            )}
            {markerPoistion ? <Marker position={markerPoistion} /> : null}
          </GoogleMap>
        </div>
      ) : <div>Loading...</div>}

      <div className="polyline-drawing-interaction-btns">
        <RippleButton disable={!enableStopBtn} className={`stop-drawing-btn ${!enableStopBtn ? "disabled" : ""}`} onClick={isDrawingActive ? stopDrawing : resumeDrawing}>
          {isDrawingActive ? "Stop" : "Resume"} Route Creation
        </RippleButton>

        <RippleButton disable={!enableStopBtn} className={`clear-drawing-btn ${!enableStopBtn ? "disabled" : ""}`} onClick={clearDrawing}>
          Clear
        </RippleButton>
      </div>
    </div>
  );
};

export default GooglePolylineDrawer;